import { Routing, usePaginationQuery } from '../Routes';
import { Main } from '../Main/Main';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { AutoPagination } from '../Pagination/Pagination';
import { useFormatting } from '../../i18n/i18n';
import { useTranslation } from '../../i18n/i18n';
import { LoaderPage } from './LoaderPage';
import { Res, useApi } from '../../state/api';
import { ErrorPage } from './ErrorPage';
import { Table } from '../Table/Table';
import { orderStatusSummary } from '../../utils/orderStatusSummary';
import { OrderCard } from '../../components/OrderCard/OrderCard';
import { AlertMessages, useAlerts } from '../../components/AlertMessages/AlertMessages';
import { useEffect } from 'react';
import { Icon, close, info, exclamation_point } from '../Icon/Icon';
import { ContentCard } from '../ContentCard/ContentCard';
import { Button } from '../Form/Button';
import { Modal } from '../Modal/Modal';
import { AccountPageNavigation } from '../AccountPageNavigation/AccountPageNavigation';
import './OrdersPage.css';

const pageSize = 50;

export const OrdersPage = (): JSX.Element => {
	const history = useHistory();
	const { asPrice, dateTimeFormat } = useFormatting();
	const t = useTranslation().orderPages;
	const tRoutes = useTranslation().routes;
	const { offset, limit } = usePaginationQuery(pageSize);
	const { data, error, isLoading } = useApi(Res.orders, { offset, limit });
	const alerts = useAlerts();
	const locationState = useLocation<{ cancelOrderSuccess?: boolean; editForbidden?: boolean }>().state || {};

	useEffect(() => {
		if (locationState.cancelOrderSuccess) {
			const cancelOrderAlertAlreadyShown = alerts.alerts.some(
				(alert) => alert.type === 'success' && alert.message === t.orderCancelled
			);
			if (!cancelOrderAlertAlreadyShown) {
				alerts.add({ type: 'success', message: t.orderCancelled });
			}
			locationState.cancelOrderSuccess = false;
			history.replace({ ...history.location });
		}
	}, []);

	const resetUpdatedState = () => {
		if (locationState) {
			locationState.editForbidden = false;
			history.replace({ ...history.location });
		}
	};

	if (isLoading) return <LoaderPage />;
	if (error) return <ErrorPage />;

	if (!data) return <Main>{t.noOrdersYet}</Main>;

	const { orders } = data;

	if (orders.length === 0)
		return (
			<Main>
				<h1>{t.yourOrders}</h1>
				<p>{t.noOrdersYet}</p>
			</Main>
		);

	return (
		<Main>
			<div id="orders-page">
				<AccountPageNavigation />
				<h1 className="private-page-heading">
					{t.yourOrders}
					{offset > 0 ? ' - sida ' + (offset / limit + 1) : ''}
				</h1>
				<AlertMessages alerts={alerts} />
				<Table className="not-mobile" striped interactive>
					<thead>
						<tr>
							<th>{t.orderNumber}</th>
							<th>{t.date}</th>
							<th>{t.recipient}</th>
							<th>{t.orderValue}</th>
							<th>{t.status}</th>
						</tr>
					</thead>
					<tbody>
						{orders.map((order) => {
							const {
								id,
								displayId,
								datePurchased,
								delivery: { firstname, lastname, city },
								totals,
							} = order;

							return (
								<tr
									key={id}
									onClick={() =>
										history.push({
											pathname: `${tRoutes.yourOrders}/${id}`,
											state: { displayId },
										})
									}
								>
									<td className="not-mobile">
										<Link
											to={{
												pathname: `${tRoutes.reorder}/${id}`,
												state: { displayId: displayId },
											}}
											onClick={(e) => e.preventDefault()}
										>
											{!!order.subscriptionOrder && (
												<span title={t.createdViaSubscription}>
													<Icon shape={info} />
												</span>
											)}
											{displayId}
										</Link>
									</td>
									<td>{dateTimeFormat(new Date(datePurchased))}</td>
									<td className="not-mobile">{`${firstname} ${lastname}, ${city}`}</td>
									<td>{asPrice(totals, 2)}</td>
									<td>{orderStatusSummary(order, t)}</td>
								</tr>
							);
						})}
					</tbody>
				</Table>
				<div className="only-mobile">
					{orders.map((order) => (
						<OrderCard key={order.id} order={order} />
					))}
				</div>

				<AutoPagination data={data} />

				{!!locationState.editForbidden && (
					<Modal className="edit-forbidden-modal" show={locationState.editForbidden} onDismiss={() => ({})}>
						<ContentCard className="edit-forbidden-content">
							<button className="close-btn" onClick={resetUpdatedState}>
								<Icon shape={{ ...close, fill: 'black' }} size={20} />
							</button>

							<div className="edit-forbidden-content-main">
								<span className="edit-forbidden-icon">
									<Icon shape={exclamation_point} size={44} />
								</span>
								<p className="edit-forbidden-title">{t.cannotEdit}</p>
								<div>
									<Button onClick={resetUpdatedState}>{t.modal.ok}</Button>
								</div>
							</div>
						</ContentCard>
					</Modal>
				)}
			</div>
		</Main>
	);
};

export const ordersRouting: Routing = {
	path(urls) {
		return urls.yourOrders;
	},
	component: OrdersPage,
	requiresAuth: true,
};
